/* eslint-env es2020 */
// zpost-app/src/components/ensapp/ProfileRecordsManager.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { JsonRpcProvider, namehash, getAddress, Interface } from 'ethers';
import { useAuth } from '../../contexts/AuthContext';
import useBridgeTransaction from '../../hooks/useBridgeTransaction';

const PUBLIC_RESOLVER_ADDRESS = '0x231b0Ee14048e9dCcD1d247744d114a4EB5E8E63';

// ==================== SIMPLE FREE RPC CONFIGURATION ====================
const FREE_RPC_ENDPOINTS = [
  'https://rpc.ankr.com/eth/dec8e5317af4d9c8cb41cc5bfaef26700319860751215bc62794cd3cafdd691d',
  'https://ethereum.publicnode.com',
  'https://cloudflare-eth.com'
];

const ZPOST_ORIGIN = 'https://zpost.ai';

const getRPCProvider = async () => {
  console.log('Getting RPC provider...');
  for (const endpoint of FREE_RPC_ENDPOINTS) {
    try {
      console.log(`Trying: ${endpoint}`);
      const provider = new JsonRpcProvider(endpoint);
      const networkPromise = provider.getNetwork();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), 3000)
      );
      await Promise.race([networkPromise, timeoutPromise]);
      console.log(`Connected to: ${endpoint}`);
      return provider;
    } catch (error) {
      console.log(`Failed: ${endpoint} - ${error.message}`);
    }
  }
  console.log('All RPCs failed, using fallback');
  return new JsonRpcProvider(FREE_RPC_ENDPOINTS[0]);
};
// ==================== END RPC CONFIGURATION ====================

const ProfileRecordsManager = ({ identityAuth, ensureSessionToken = null }) => {
  const { walletAddress, ensName, isAuthenticated } = identityAuth || {};

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [transactionStatus, setTransactionStatus] = useState('');

  // ENS domains
  const [allEnsNames, setAllEnsNames] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState('');

  // Profile fields
  const [profile, setProfile] = useState({
    description: '',
    url: '',
    'com.twitter': '',
    'com.github': '',
    'org.telegram': '',
    'com.discord': '',
    location: '',
    email: ''
  });
  const [recordsToUpdate, setRecordsToUpdate] = useState({});

  // RPC provider
  const [stableProvider, setStableProvider] = useState(null);

  // Hooks
  const { wallet: authWallet, connectToZwallet } = useAuth();
  const { sendTransaction: bridgeSend } = useBridgeTransaction();

  // Receipt modal state
  const [receiptTxHash, setReceiptTxHash] = useState(null);

  // Ref to keep authWallet up‑to‑date in async callbacks
  const authWalletRef = useRef(authWallet);
  useEffect(() => {
    authWalletRef.current = authWallet;
    console.log('authWalletRef updated:', authWallet?.isConnected);
  }, [authWallet]);

  const validNameRegex = useMemo(() => /^(?!.*\s)(?!.*\[)(?!.*\]).+\..+$/, []);

  // Initialize provider
  useEffect(() => {
    let cancelled = false;
    const initProvider = async () => {
      try {
        const provider = await getRPCProvider();
        if (!cancelled) setStableProvider(provider);
      } catch (err) {
        console.error('Failed to initialize RPC provider:', err);
        setError('Could not connect to Ethereum network. Please refresh.');
      }
    };
    initProvider();
    return () => { cancelled = true; };
  }, []);

  // Helper to normalize transaction (kept from original)
  const normalizeTx = (tx) => {
    if (!tx || typeof tx !== 'object') return tx;
    const out = { ...tx };
    if (out.to && typeof out.to !== 'string') {
      try { out.to = String(out.to); } catch (e) { /* ignore */ }
    }
    try { if (out.to) out.to = getAddress(out.to); } catch (e) { /* ignore */ }
    if (out.data && typeof out.data === 'string' && !out.data.startsWith('0x')) {
      out.data = '0x' + out.data;
    }
    const v = out.value;
    if (v === undefined || v === null || v === '') {
      out.value = '0x0';
    } else if (typeof v === 'string') {
      const s = v.trim();
      if (s.startsWith('0x') || s.startsWith('0X')) {
        out.value = s;
      } else if (s.indexOf('.') >= 0) {
        const n = Number(s);
        if (!isNaN(n)) {
          try { out.value = '0x' + BigInt(Math.round(n * 1e18)).toString(16); } catch (e) { out.value = '0x0'; }
        } else {
          try { out.value = '0x' + BigInt(s).toString(16); } catch (e) { out.value = '0x0'; }
        }
      } else {
        try { out.value = '0x' + BigInt(s).toString(16); } catch (e) { out.value = '0x0'; }
      }
    } else if (typeof v === 'number') {
      try { out.value = '0x' + BigInt(Math.floor(v)).toString(16); } catch (e) { out.value = '0x0'; }
    } else if (typeof v === 'bigint') {
      out.value = '0x' + v.toString(16);
    } else {
      out.value = '0x0';
    }
    if (!out.chainId) out.chainId = '0x1';
    else if (typeof out.chainId === 'number') out.chainId = '0x' + Number(out.chainId).toString(16);
    else if (typeof out.chainId === 'string' && !out.chainId.startsWith('0x')) {
      try { out.chainId = '0x' + BigInt(out.chainId).toString(16); } catch (e) { out.chainId = '0x1'; }
    }
    if (!out.from && walletAddress) out.from = walletAddress;
    if (out.from) out.from = String(out.from);
    if (!out.gasLimit && !out.gas) out.gasLimit = '0x186a0';
    if (!out.gasPrice && !out.maxFeePerGas && !out.maxPriorityFeePerGas) {
      out.gasPrice = '0x2540be400';
    }
    return out;
  };

  // Fetch domains from TheGraph
  const fetchNamesFromENS = useCallback(async (walletAddr) => {
    if (!walletAddr || !stableProvider) return [];
    const SUBGRAPH_URL = 'https://api.thegraph.com/subgraphs/name/ensdomains/ens';
    const query = `
      query GetDomains($owner: String!) {
        domains(where: { owner: $owner }, first: 200, orderBy: createdAt, orderDirection: desc) {
          id
          name
          labelName
          resolver { id address }
        }
      }
    `;
    const callSubgraph = async () => {
      const resp = await fetch(SUBGRAPH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, variables: { owner: walletAddr.toLowerCase() } })
      });
      const text = await resp.text().catch(() => null);
      let json = null;
      try { json = text ? JSON.parse(text) : null; } catch (e) { json = null; }
      if (!resp.ok) throw new Error(`GraphQL HTTP ${resp.status}`);
      if (json && json.errors) throw new Error(json.errors[0]?.message || 'GraphQL error');
      const domains = (json && json.data && json.data.domains) ? json.data.domains : [];
      return domains.map(d => ({
        name: d.name,
        labelName: d.labelName,
        id: d.id,
        hasResolver: !!(d.resolver && d.resolver.address)
      }));
    };

    try {
      let domains = [];
      try { domains = await callSubgraph(); } catch (firstErr) {
        console.warn('fetchNamesFromENS: first subgraph attempt failed:', firstErr);
        await new Promise(res => setTimeout(res, 700));
        try { domains = await callSubgraph(); } catch (secondErr) { domains = []; }
      }
      const filtered = (domains || []).filter(d => d.name && typeof d.name === 'string' && validNameRegex.test(d.name));
      if (filtered.length > 0) return filtered;

      try {
        const reverseName = await stableProvider.lookupAddress(walletAddr);
        if (reverseName && validNameRegex.test(reverseName)) {
          let hasResolver = false;
          try { const resolver = await stableProvider.getResolver(reverseName); hasResolver = !!resolver; } catch (e) { hasResolver = false; }
          return [{ name: reverseName, labelName: reverseName.split('.')[0], id: `reverse-${reverseName}`, hasResolver }];
        }
      } catch (e) {
        console.warn('stableProvider.lookupAddress failed (fallback):', e);
      }
      return [];
    } catch (err) {
      console.error('fetchNamesFromENS encountered an error:', err);
      throw err;
    }
  }, [stableProvider, validNameRegex]);

  // Fetch current records for a domain
  const fetchCurrentRecords = useCallback(async (domainName) => {
    if (!domainName || !stableProvider) {
      setError('No domain selected for fetching records');
      return;
    }
    if (!validNameRegex.test(domainName)) {
      console.warn('fetchCurrentRecords: rejected invalid ENS name:', domainName);
      setError('Invalid ENS name returned from lookup; please select a different domain');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const textRecordKeys = ['description', 'url', 'com.twitter', 'com.github', 'org.telegram', 'com.discord', 'location', 'email'];
      const resolver = await stableProvider.getResolver(domainName);
      if (!resolver) {
        setProfile(prev => ({ ...prev }));
        setIsLoading(false);
        return;
      }
      const recordPromises = textRecordKeys.map(async (k) => {
        try { const v = await resolver.getText(k); return { k, v }; } catch (e) { return { k, v: '' }; }
      });
      const results = await Promise.all(recordPromises);
      const records = {};
      results.forEach(({ k, v }) => { records[k] = v || ''; });
      setProfile({
        description: records.description || '',
        url: records.url || '',
        'com.twitter': records['com.twitter'] || '',
        'com.github': records['com.github'] || '',
        'org.telegram': records['org.telegram'] || '',
        'com.discord': records['com.discord'] || '',
        location: records.location || '',
        email: records.email || ''
      });
      setError('');
    } catch (err) {
      console.error('Failed to fetch current records:', err);
      setError('Failed to load current profile records');
    } finally {
      setIsLoading(false);
    }
  }, [stableProvider, validNameRegex]);

  // Initialise component: load primary ENS name
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      if (!stableProvider) return;
      setIsLoading(true);
      setError('');
      try {
        if (!walletAddress) {
          setAllEnsNames([]);
          setSelectedDomain('');
          setIsLoading(false);
          return;
        }
        if (ensName && typeof ensName === 'string' && validNameRegex.test(ensName)) {
          const primary = { name: ensName, labelName: ensName.split('.')[0], id: `primary-${ensName}`, hasResolver: true };
          if (!cancelled) {
            setAllEnsNames([primary]);
            setSelectedDomain(prev => prev || primary.name);
            await fetchCurrentRecords(primary.name);
          }
          return;
        }
        try {
          const reverse = await stableProvider.lookupAddress(walletAddress);
          if (reverse && validNameRegex.test(reverse)) {
            const primary = { name: reverse, labelName: reverse.split('.')[0], id: `reverse-${reverse}`, hasResolver: true };
            if (!cancelled) {
              setAllEnsNames([primary]);
              setSelectedDomain(prev => prev || primary.name);
              await fetchCurrentRecords(primary.name);
            }
            return;
          }
        } catch (e) {
          console.warn('Reverse lookup failed during init:', e);
        }
        if (!cancelled) {
          setAllEnsNames([]);
          setSelectedDomain('');
        }
      } catch (e) {
        console.error('Initialization error in ProfileRecordsManager:', e);
        setError('Failed to initialize ENS domain info');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, [walletAddress, ensName, fetchCurrentRecords, stableProvider, validNameRegex]);

  // Debug logger
  useEffect(() => {
    console.log('ProfileRecordsManager state:', {
      walletAddress,
      ensName,
      selectedDomain,
      allEnsNames,
      isSaving,
      recordsToUpdate,
      transactionStatus
    });
  }, [walletAddress, ensName, selectedDomain, allEnsNames, isSaving, recordsToUpdate, transactionStatus]);

  // Discover other domains owned by wallet
  const discoverOtherDomains = useCallback(async () => {
    if (!walletAddress) { setError('Wallet not connected'); return; }
    if (!stableProvider) { setError('Network not ready'); return; }
    setIsDiscovering(true);
    setError('');
    try {
      const domains = await fetchNamesFromENS(walletAddress);
      if (!domains || domains.length === 0) {
        setError('No additional ENS domains found for this wallet');
        setAllEnsNames([]);
        return;
      }
      const sorted = domains.sort((a, b) => (a.name === ensName ? -1 : 0));
      setAllEnsNames(sorted);
      if (!selectedDomain || !sorted.find(d => d.name === selectedDomain)) {
        setSelectedDomain(sorted[0].name);
        await fetchCurrentRecords(sorted[0].name);
      }
    } catch (err) {
      console.error('Discover other domains failed:', err);
      if (err && typeof err.message === 'string' && err.message.includes('429')) {
        setError('The domain discovery service is rate-limited. Try again later.');
      } else {
        setError('Failed to discover domains. Try again.');
      }
    } finally {
      setIsDiscovering(false);
    }
  }, [walletAddress, fetchNamesFromENS, ensName, selectedDomain, fetchCurrentRecords, stableProvider]);

  // Core function to send a single transaction via the bridge
  const sendSingleTransaction = async (key, textValue, onSuccess, onError) => {
    if (!selectedDomain) {
      onError(new Error('No domain selected'));
      return;
    }
    if (!stableProvider) {
      onError(new Error('Network not ready'));
      return;
    }

    // We'll store the resolver address from verification
    let domainResolverAddress = null;

    // Verify domain ownership and capture resolver address
    try {
      console.log(`🔍 Verifying ownership of ${selectedDomain} for ${walletAddress}`);
      const resolvedAddress = await stableProvider.resolveName(selectedDomain);
      if (!resolvedAddress || resolvedAddress.toLowerCase() !== walletAddress.toLowerCase()) {
        throw new Error(`Domain ${selectedDomain} does not resolve to your wallet address. It resolves to: ${resolvedAddress || 'nothing'}`);
      }

      const resolver = await stableProvider.getResolver(selectedDomain);
      if (!resolver) {
        throw new Error(`No resolver configured for ${selectedDomain}`);
      }

      // Get the resolver contract address
      domainResolverAddress = resolver.address;
      console.log(`🔍 Resolver contract address: ${domainResolverAddress}`);

      // Also verify that the resolver points to the owner (optional, but we keep it)
      const resolverEthAddress = await resolver.getAddress();
      if (!resolverEthAddress || resolverEthAddress.toLowerCase() !== walletAddress.toLowerCase()) {
        throw new Error(`Resolver reports different address for ${selectedDomain}`);
      }

      console.log(`✅ Domain ownership verified: ${selectedDomain} -> ${walletAddress}`);
    } catch (verifyErr) {
      console.error('Domain ownership verification failed:', verifyErr);
      setIsSaving(false);
      setTransactionStatus('');
      setError(`Cannot set profile records: ${verifyErr.message}`);
      onError(verifyErr);
      return;
    }

    setIsSaving(true);
    setTransactionStatus('preparing');
    setError('');
    setSuccess('');

    const messageId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `msg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    // Build transaction payload using the domain's resolver address
    const buildTxPayload = async () => {
      const node = namehash(selectedDomain);
      const resolverAddress = domainResolverAddress; // Use the correct resolver
      console.log(`🔧 Using resolver for text records: ${resolverAddress} for domain: ${selectedDomain}`);

      try {
        const code = await stableProvider.getCode(resolverAddress);
        if (!code || code === '0x') {
          throw new Error(`Resolver contract not found at address ${resolverAddress}`);
        }
      } catch (codeErr) {
        throw new Error(`Resolver check failed: ${codeErr?.message || String(codeErr)}`);
      }

      try {
        const iface = new Interface([
          "function setText(bytes32,string,string)"
        ]);
        const data = iface.encodeFunctionData('setText', [node, key, String(textValue)]);
        const fallbackPayload = {
          to: resolverAddress,
          data,
          value: '0x0'
        };
        console.log('🔍 FINAL TRANSACTION DESTINATION:', fallbackPayload.to);
        return { txPayload: fallbackPayload };
      } catch (err) {
        console.error('Error creating fallback payload:', err);
        throw new Error(`Failed to create transaction: ${err.message}`);
      }
    };

    let txPayload;
    try {
      ({ txPayload } = await buildTxPayload());
    } catch (buildErr) {
      console.error('[sendSingleTransaction] Failed to build/prepare tx:', buildErr);
      setIsSaving(false);
      setTransactionStatus('');
      setError(buildErr?.message || String(buildErr));
      onError(buildErr);
      return;
    }

    const normalizedTxPayload = (() => {
      try {
        const n = normalizeTx(txPayload || {});
        if (!n.chainId) n.chainId = '0x1';
        if (!n.from && walletAddress) n.from = walletAddress;
        if (n.to) try { n.to = String(n.to); } catch (e) {}
        return n;
      } catch (e) {
        console.debug('[sendSingleTransaction] normalization fallback failed', e);
        return txPayload || {};
      }
    })();

    const normalizedRaw = { ...normalizedTxPayload };

    // Pre‑flight gas check (optional)
    let preflightGasEstimate = null;
    try {
      preflightGasEstimate = await stableProvider.estimateGas({
        to: normalizedRaw.to,
        data: normalizedRaw.data,
        value: normalizedRaw.value || '0x0',
        from: normalizedRaw.from || walletAddress
      });
    } catch (preErr) {
      console.warn('[preflight] gas estimation failed (continuing):', preErr);
    }

    // Final transaction object for the bridge
    const tx = {
      to: normalizedRaw.to,
      data: normalizedRaw.data,
      value: normalizedRaw.value || '0x0',
      from: normalizedRaw.from || walletAddress,
      ...(preflightGasEstimate ? { gasLimit: preflightGasEstimate } : {})
    };

    // Send via bridge
    try {
      console.log('🔑 Using bridge transaction');
      const result = await bridgeSend(tx);
      console.log('✅ Transaction confirmed:', result);

      // Show receipt modal with block number and Etherscan link
      setReceiptTxHash({
        hash: result.hash,
        blockNumber: result.blockNumber
      });

      setIsSaving(false);
      setTransactionStatus('confirmed');
      onSuccess();
      setTimeout(() => {
        if (selectedDomain) fetchCurrentRecords(selectedDomain).catch(() => {});
      }, 5000);
    } catch (err) {
      console.error('Transaction failed:', err);
      setIsSaving(false);
      setTransactionStatus('failed');
      setError(err.message || 'Transaction failed');
      onError(err);
    }
  };

  // Wrapper to be called from handleSave
  const sendTransaction = async (key, textValue) => {
    if (!walletAddress) throw new Error('Wallet not connected');
    if (!authWalletRef.current?.isConnected) {
      throw new Error('Zwallet not ready. Please unlock your wallet first.');
    }
    return new Promise((resolve, reject) => {
      sendSingleTransaction(key, textValue, resolve, reject);
    });
  };

  // Save handler – triggers unlock if needed, then sends transaction(s)
  const handleSave = async () => {
    if (!selectedDomain) { setError('Please select an ENS domain'); return; }
    if (!recordsToUpdate || Object.keys(recordsToUpdate).length === 0) { setError('No changes to save'); return; }

    setError('');
    setIsSaving(true);
    setTransactionStatus('Preparing transaction...');

    // If wallet not connected, open unlock modal
    if (!authWalletRef.current?.isConnected) {
      console.log('Wallet not connected, opening wallet modal via connectToZwallet');
      connectToZwallet();
    }

    let walletConnected = false;
    console.log('⏳ Waiting for wallet to connect (max 300 seconds)...');
    for (let attempt = 0; attempt < 300; attempt++) {
      if (authWalletRef.current?.isConnected) {
        walletConnected = true;
        console.log(`✅ Wallet connected after ${attempt} seconds`);
        break;
      }
      if (attempt % 30 === 0 && attempt > 0) {
        console.log(`Still waiting for wallet connection... ${attempt} seconds elapsed, authWalletRef.current?.isConnected = ${authWalletRef.current?.isConnected}`);
      }
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    if (!walletConnected) {
      setError('Wallet not ready. Please unlock your wallet and try again.');
      setIsSaving(false);
      setTransactionStatus('');
      return;
    }

    try {
      const keys = Object.keys(recordsToUpdate);
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        await sendTransaction(k, recordsToUpdate[k]);
      }

      setRecordsToUpdate({});
      setTransactionStatus('confirmed');
      // Refresh records after a delay to reflect on‑chain changes (receipt modal stays)
      setTimeout(() => {
        if (selectedDomain) fetchCurrentRecords(selectedDomain).catch(() => {});
      }, 5000);
    } catch (err) {
      console.error('Error saving profile records:', err);
      setError(`Failed to save profile: ${err.message}`);
      setIsSaving(false);
      setTransactionStatus('');
    }
  };

  // Input handlers
  const handleDomainChange = (domainName) => {
    setError('');
    setSuccess('');
    setRecordsToUpdate({});
    setSelectedDomain(domainName);
  };

  const handleInputChange = useCallback((field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    setRecordsToUpdate(prev => ({ ...prev, [field]: value }));
  }, []);

  const clearField = useCallback((field) => {
    setProfile(prev => ({ ...prev, [field]: '' }));
    setRecordsToUpdate(prev => ({ ...prev, [field]: '' }));
  }, []);

  const resetChanges = () => {
    if (selectedDomain) fetchCurrentRecords(selectedDomain).catch(() => {});
    setRecordsToUpdate({});
    setError('');
    setSuccess('');
  };

  // Render
  if (!identityAuth) {
    return (
      <div className="profile-records-manager">
        <h2>Manage Profile Records</h2>
        <div className="identity-card" style={{ textAlign: 'center', padding: '2rem', background: 'var(--black)', border: '1px solid var(--gold)' }}>
          <p style={{ color: '#ff4444' }}>Authentication required</p>
          <p style={{ color: 'var(--text-secondary)' }}>Please connect your wallet to manage profile records.</p>
        </div>
      </div>
    );
  }

  if (isLoading || !stableProvider) {
    return (
      <div className="profile-records-manager">
        <h2>Manage Profile Records</h2>
        <div className="identity-card" style={{ textAlign: 'center', padding: '3rem', background: 'var(--black)', border: '1px solid var(--gold)' }}>
          <p style={{ color: 'var(--gold)' }}>Loading your ENS names and records...</p>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '1rem' }}>Connecting to Ethereum network...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-records-manager">
      <h2>Manage Profile Records</h2>
      <p className="text-secondary" style={{ textAlign: 'center', marginBottom: '2rem' }}>
        Add social links and profile information to your domain
      </p>

      <div className="status-message" style={{ background: 'var(--black)', border: '1px solid var(--gold)', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
        <p style={{ margin: 0, fontWeight: 'bold', color: 'var(--gold)' }}>
          Ethereum L1 Mainnet: All profile record operations will create real transactions on Ethereum Mainnet.
        </p>
      </div>

      <div className="record-editor">
        <p className="text-secondary" style={{ textAlign: 'center', marginBottom: '2rem' }}>
          Add information to your ENS domains that will be visible across the ZPost ecosystem and other ENS-supporting applications.
        </p>

        {!walletAddress && (
          <div className="status-message status-error" style={{ background: 'var(--black)', border: '1px solid var(--gold)', padding: '12px', borderRadius: '8px' }}>
            <p style={{ margin: 0, color: '#ff4444' }}>Wallet not connected</p>
          </div>
        )}

        {transactionStatus && (
          <div className={`status-message`} style={{ background: 'var(--black)', border: '1px solid var(--gold)', padding: '12px', borderRadius: '8px', marginTop: '12px' }}>
            <p style={{ margin: 0, fontWeight: 'bold', color: transactionStatus === 'failed' ? '#ff4444' : 'var(--gold)' }}>
              {transactionStatus === 'pending' && 'Processing transactions...'}
              {transactionStatus === 'confirmed' && 'Transactions confirmed!'}
              {transactionStatus === 'failed' && 'Transaction failed'}
              {transactionStatus === 'wallet-not-ready' && 'Wallet locked — unlock to proceed.'}
            </p>
          </div>
        )}

        {/* Domain selector + discover button */}
        <div className="form-group" style={{ marginBottom: '2rem', display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <label className="form-label">Select ENS Domain to Manage</label>
            <select
              value={selectedDomain}
              onChange={(e) => handleDomainChange(e.target.value)}
              className="domain-input"
              style={{ width: '100%', fontSize: '16px', padding: '12px', background: 'var(--black)', border: '1px solid var(--gold)', color: 'var(--gold)' }}
            >
              <option value="">{allEnsNames.length === 0 ? 'No primary ENS detected — discover or enter a domain' : 'Choose a domain...'}</option>
              {allEnsNames.map(domain => (
                <option key={domain.id} value={domain.name}>
                  {domain.name} {!domain.hasResolver && '(No Resolver)'}
                </option>
              ))}
            </select>
            {selectedDomain && !allEnsNames.find(d => d.name === selectedDomain)?.hasResolver && (
              <p style={{ color: '#ffa500', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                This domain doesn't have a resolver configured. Some features may not work.
              </p>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '200px' }}>
            <button
              type="button"
              onClick={discoverOtherDomains}
              disabled={isDiscovering || !walletAddress}
              className="btn btn-gold"
              style={{
                background: isDiscovering || !walletAddress ? '#555' : 'var(--gold)',
                color: isDiscovering || !walletAddress ? '#ccc' : '#000',
                border: 'none',
                padding: '10px 16px',
                borderRadius: '8px',
                fontWeight: 'bold',
                cursor: isDiscovering || !walletAddress ? 'not-allowed' : 'pointer'
              }}
            >
              {isDiscovering ? 'Discovering...' : 'Discover other domains'}
            </button>

            <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
              Click to list all ENS domains your wallet owns (on-demand to avoid rate-limits).
            </small>
          </div>
        </div>

        {!selectedDomain && allEnsNames.length > 0 && (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)', background: 'var(--black)', border: '1px solid var(--gold)' }}>
            Please select an ENS domain to manage its profile records
          </div>
        )}

        {selectedDomain && (
          <div className="profile-form">
            <div className="status-message" style={{ textAlign: 'center', background: 'var(--black)', border: '1px solid var(--gold)', padding: '12px', borderRadius: '8px' }}>
              <p style={{ margin: '0', fontWeight: 'bold', fontSize: '16px', color: 'var(--gold)' }}>
                Managing: <span style={{ color: 'var(--gold)' }}>{selectedDomain}</span>
              </p>
              <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: 'var(--text-secondary)' }}>
                Changes will create real Ethereum transactions requiring gas fees
              </p>
            </div>

            <div className="form-group" style={{ marginTop: '2rem' }}>
              <label className="form-label">Bio / Description</label>
              <textarea
                value={profile.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Tell the ZPost community about yourself..."
                maxLength={500}
                rows={4}
                className="domain-input"
                style={{
                  width: '100%',
                  minHeight: '120px',
                  fontSize: '16px',
                  padding: '15px',
                  resize: 'vertical',
                  background: 'var(--black)',
                  border: '1px solid var(--gold)',
                  color: 'var(--gold)'
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                  {profile.description.length}/500 characters
                </span>
                {profile.description && (
                  <button
                    type="button"
                    onClick={() => clearField('description')}
                    style={{
                      background: 'transparent',
                      color: '#ff4444',
                      border: '1px solid #ff4444',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: '3px'
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', marginTop: '2rem' }}>
              <div className="form-group">
                <label className="form-label">Website</label>
                <input
                  type="url"
                  value={profile.url}
                  onChange={(e) => handleInputChange('url', e.target.value)}
                  placeholder="https://yourwebsite.com"
                  className="domain-input"
                  style={{ width: '100%', fontSize: '16px', padding: '12px', background: 'var(--black)', border: '1px solid var(--gold)', color: 'var(--gold)' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">X (Twitter)</label>
                <input
                  type="text"
                  value={profile['com.twitter']}
                  onChange={(e) => handleInputChange('com.twitter', e.target.value)}
                  placeholder="username (without @)"
                  className="domain-input"
                  style={{ width: '100%', fontSize: '16px', padding: '12px', background: 'var(--black)', border: '1px solid var(--gold)', color: 'var(--gold)' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">GitHub</label>
                <input
                  type="text"
                  value={profile['com.github']}
                  onChange={(e) => handleInputChange('com.github', e.target.value)}
                  placeholder="github username"
                  className="domain-input"
                  style={{ width: '100%', fontSize: '16px', padding: '12px', background: 'var(--black)', border: '1px solid var(--gold)', color: 'var(--gold)' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Telegram</label>
                <input
                  type="text"
                  value={profile['org.telegram']}
                  onChange={(e) => handleInputChange('org.telegram', e.target.value)}
                  placeholder="telegram handle (without @)"
                  className="domain-input"
                  style={{ width: '100%', fontSize: '16px', padding: '12px', background: 'var(--black)', border: '1px solid var(--gold)', color: 'var(--gold)' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Discord</label>
                <input
                  type="text"
                  value={profile['com.discord']}
                  onChange={(e) => handleInputChange('com.discord', e.target.value)}
                  placeholder="discord handle or id"
                  className="domain-input"
                  style={{ width: '100%', fontSize: '16px', padding: '12px', background: 'var(--black)', border: '1px solid var(--gold)', color: 'var(--gold)' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Location</label>
                <input
                  type="text"
                  value={profile.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder="City, Country"
                  className="domain-input"
                  style={{ width: '100%', fontSize: '16px', padding: '12px', background: 'var(--black)', border: '1px solid var(--gold)', color: 'var(--gold)' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="you@example.com"
                  className="domain-input"
                  style={{ width: '100%', fontSize: '16px', padding: '12px', background: 'var(--black)', border: '1px solid var(--gold)', color: 'var(--gold)' }}
                />
              </div>
            </div>

            {/* Save / Reset actions */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button
                type="button"
                onClick={resetChanges}
                disabled={isSaving || Object.keys(recordsToUpdate || {}).length === 0}
                style={{
                  background: 'transparent',
                  color: '#ff4444',
                  border: '1px solid #ff4444',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  cursor: isSaving || Object.keys(recordsToUpdate || {}).length === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                Reset
              </button>

              <button
                type="button"
                onClick={handleSave}
                className="btn btn-gold"
                disabled={isSaving || Object.keys(recordsToUpdate || {}).length === 0}
                style={{
                  background: isSaving || Object.keys(recordsToUpdate || {}).length === 0 ? '#555' : 'var(--gold)',
                  color: isSaving || Object.keys(recordsToUpdate || {}).length === 0 ? '#ccc' : '#000',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: isSaving || Object.keys(recordsToUpdate || {}).length === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div style={{ marginTop: '12px', padding: '10px', borderRadius: '8px', background: '#2b1212', border: '1px solid #ff4444' }}>
            <p style={{ margin: 0, color: '#ffb3b3' }}>{error}</p>
          </div>
        )}
      </div>

      {/* Receipt Modal */}
      {receiptTxHash && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.95)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--card-bg)',
            padding: '30px',
            borderRadius: '15px',
            border: '2px solid var(--gold)',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 0 50px rgba(255,215,0,0.3)'
          }}>
            <h3 style={{ color: 'var(--gold)', margin: '0 0 20px 0', fontSize: '24px', textAlign: 'center' }}>
              Transaction Confirmed
            </h3>
            <div style={{
              background: 'var(--black)',
              padding: '20px',
              borderRadius: '10px',
              border: '1px solid var(--gold)',
              marginBottom: '20px'
            }}>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '15px' }}>
                Your transaction has been successfully mined.
              </p>
              {receiptTxHash.blockNumber && (
                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ color: 'var(--gold)' }}>Block:</strong>
                  <span style={{ color: 'var(--text-light)', marginLeft: '5px' }}>
                    {receiptTxHash.blockNumber}
                  </span>
                </div>
              )}
              <div style={{
                background: 'rgba(255,215,0,0.1)',
                padding: '10px',
                borderRadius: '5px',
                wordBreak: 'break-all',
                marginBottom: '15px'
              }}>
                <a
                  href={`https://etherscan.io/tx/${receiptTxHash.hash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--gold)', textDecoration: 'underline' }}
                >
                  {receiptTxHash.hash}
                </a>
              </div>
              <a
                href={`https://etherscan.io/tx/${receiptTxHash.hash}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'block',
                  textAlign: 'center',
                  background: 'var(--gold)',
                  color: 'var(--black)',
                  padding: '10px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  marginBottom: '10px'
                }}
              >
                View on Etherscan
              </a>
            </div>
            <button
              onClick={() => setReceiptTxHash(null)}
              style={{
                width: '100%',
                background: 'transparent',
                color: '#ff4444',
                border: '1px solid #ff4444',
                padding: '15px',
                borderRadius: '8px',
                fontWeight: 'bold',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileRecordsManager;
